<template>
  <div
    ref="recommendedProducts"
    class="recommended-products"
    :class="{ 'is-loading' : state !== 'ready' }"
  >
    <heading
      v-if="headingText"
      class="recommended-products__heading"
      :type="headingType"
      :text="headingText"
    />

    <bloomreach-recommendations
      v-if="state === 'ready'"
      context="default"
      :tracking-parameters="trackingParameters"
      :section-id="liquid.sectionId"
      :recommendation-id="recommendation_id"
      :recommendation-size="recommendationSize"
      :category-name="categoryName"
      :initial-item-id="itemId"
      :slides-per-view="{
        mobile: 1.25,
        tablet: 2.22,
        desktop: 3,
        wide: 4
      }"
      @recommendations-not-loaded="destroyComponent"
    />
  </div>
</template>

<script>

/**
 * Section: Recommended Products
 * ------------------------------------------------------------------------------
 * - A section with a carousel of 'Recommended' products cards.
 * @param {String} headingText - Heading Text to display.
 * @param {String} headingType - Theme Settings for h1-h6 values.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Object} trackingParameters - Object of parameters, typically added to events (Bloomreach, Attraqt, Google).
 * @param {String} trackingParameters.section - Unique name of the section.
 * @param {String|Boolean} categoryName - Handle of the collection, used as a Bloomreach attribute.
 * @param {String|Number|Boolean} itemId - Product ID, used as a Bloomreach attribute.
 * @param {Number} recommendationSize - Limit of products to show in the carousel.
 * @param {String} recommendationId - Engine ID of an existing recommendation model. This ID is created when the engine is saved in Bloomreach Engagement.
 *
 */
import { isVisible, setIntersectionObserver } from '~/helpers/dom'
import { importComponent } from '~/core/mint/helpers'

import Heading from '~components/heading/heading'

export default {
  name: 'RecommendedProducts',

  components: {
    Heading,
    BloomreachRecommendations: importComponent('bloomreach-recommendations'),
  },

  props: {
    headingText: {
      type: String,
      default: '',
    },
    headingType: {
      type: String,
      default: 'h2',
    },
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    categoryName: {
      type: [Boolean, String],
      default: false,
    },
    itemId: {
      type: [
        Boolean, String, Number,
      ],
      default: false,
    },
    recommendationId: {
      type: String,
      default: '',
    },
    recommendationSize: {
      type: Number,
      default: 8,
    },
  },

  data() {
    return {
      state: 'loading',

      /**
       * Reassign the recommendation id to the data of this component to be able
       * to change it dynamically in the future
       */
      recommendation_id: this.recommendationId,
    }
  },

  mounted() {
    this.initializeSectionObserver()
  },

  methods: {

    /**
     * Initializes the observer for the recommended products section.
     */
    initializeSectionObserver() {
      const targetElement = this.$refs.recommendedProducts

      if (isVisible(targetElement)) {
        this.state = 'ready'
        return
      }

      setIntersectionObserver({
        callback: this.handleSectionIntersection,
        root: targetElement.parentNode,
        rootMargin: '20px 0px',
        element: targetElement,
        threshold: 0.1,
      })
    },

    /**
     * Handle event when a section is scrolled into view.
     * @param {Array} entries - Array of Intersection Observer entries.
     * @param {Object} observer - Intersection Observer.
     */
    handleSectionIntersection(entries, observer) {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return
        }

        /**
         * The section already was in the viewport.
         */
        if (this.state === 'ready') {
          observer.disconnect()
          return
        }

        this.state = 'ready'
      })
    },

    /**
     * A method that is called when the products recommendations did not load.
     * Remove and unmount the component.
     * @function destroyComponent
     */
    destroyComponent() {
      const parentSection = this.$el.closest('.shopify-section')

      parentSection?.remove()
    },
  },
}
</script>

<style src="./recommended-products.scss" lang="scss"></style>
